// ============================================================================
// CRM DRAWER — side panel for one booking on the Studio CRM screen:
// booking summary (with the shared BookingModal for full view/edit), the
// Dynamics account, and linked opportunities.
// ============================================================================

import React, { useEffect, useState } from 'react';
import {
  X, Building2, Check, Loader, Plus, Pencil, Trash2, ExternalLink, AlertTriangle, CalendarDays, MapPin,
  User, Tag, Briefcase, PenSquare, Ban, RotateCcw
} from 'lucide-react';
import { toast } from 'sonner';
import { StudioBooking, STATUS_LABELS, STATUS_COLORS, DATE_TYPE_LABELS } from '../../services/studio-bookings';
import {
  CrmBooking,
  BookingAccountLink,
  BookingOpportunityLink,
  BookingCrmExclusion,
  CrmExclusionReason,
  CrmNotesSummary,
  EXCLUSION_REASON_LABELS,
  excludeBooking,
  includeBooking,
  STATE_LABELS,
  STATE_COLORS,
  ATTRIBUTION_LABELS,
  setBookingAccount,
  clearBookingAccount,
  unlinkOpportunity,
  opportunityUrl,
  effectiveValues
} from '../../services/studio-crm';
import { formatMoney } from './crm-totals';
import CrmNotes from './CrmNotes';

export const BILLING_LABELS: Record<string, string> = { investment: 'Investment', chargeable: 'Chargeable', na: 'N/A' };

export function formatDate(ymd?: string): string {
  if (!ymd) return '—';
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

export function customerLabel(b: CrmBooking): string {
  return b.crm.account?.accountName || b.customer;
}

interface Props {
  booking: CrmBooking;
  fullBooking: StudioBooking | null;   // null when the caller's role doesn't get the full record
  accountNames: string[];
  conflictingIds: Set<string>;
  onClose: () => void;
  onAccountChanged: (account: BookingAccountLink | null) => void;
  onOpportunityRemoved: (opportunityId: string) => void;
  onAddOpportunity: () => void;
  onEditOpportunity: (link: BookingOpportunityLink) => void;
  onOpenBooking: () => void;
  onExclusionChanged: (exclusion: BookingCrmExclusion | null) => void;
  currentUser: { userId: string; canModerateNotes: boolean } | null;
  onNotesSummary: (summary: CrmNotesSummary) => void;
}

function SectionLabel({ children, action }: { children: React.ReactNode; action?: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between mb-3">
      <span className="text-[11px] font-bold text-primary tracking-widest uppercase">{children}</span>
      {action}
    </div>
  );
}

function Fact({ icon: Icon, label, value }: { icon: React.ElementType; label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start gap-2.5">
      <Icon size={14} className="text-slate-400 mt-0.5 flex-shrink-0" />
      <div className="min-w-0">
        <div className="text-[11px] text-slate-400">{label}</div>
        <div className="text-sm text-navy break-words">{value}</div>
      </div>
    </div>
  );
}

function AccountSection({ booking, accountNames, onChanged }: {
  booking: CrmBooking; accountNames: string[]; onChanged: (account: BookingAccountLink | null) => void;
}) {
  const current = booking.crm.account?.accountName || '';
  const [value, setValue] = useState(current);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setValue(current); setEditing(false); }, [current, booking.bookingId]);

  async function save(name: string) {
    if (saving) return;
    setSaving(true);
    try {
      if (name.trim()) {
        onChanged(await setBookingAccount(booking.bookingId, name.trim()));
        toast.success('Account saved');
      } else if (current) {
        await clearBookingAccount(booking.bookingId);
        onChanged(null);
        toast.success('Account cleared');
      }
      setEditing(false);
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Failed to save account');
    } finally {
      setSaving(false);
    }
  }

  if (!editing) {
    return (
      <div className="flex items-center justify-between gap-3 bg-white border border-[#e2e8f0] rounded-xl px-3 py-2.5">
        <div className="flex items-center gap-2 min-w-0">
          <Building2 size={14} className="text-slate-400 flex-shrink-0" />
          {current
            ? <span className="text-sm font-semibold text-navy truncate">{current}</span>
            : <span className="text-sm text-slate-400 italic">Not set</span>}
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {!current && (
            <button onClick={() => save(booking.customer)} disabled={saving}
              className="h-7 px-2.5 rounded-lg bg-primary/10 text-primary text-xs font-semibold hover:bg-primary/20 disabled:opacity-60">
              Use “{booking.customer}”
            </button>
          )}
          <button onClick={() => setEditing(true)} title={current ? 'Change account' : 'Enter a different account'}
            className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500"><Pencil size={14} /></button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <input autoFocus value={value} list="crm-account-names" maxLength={200} placeholder="Dynamics account name"
        onChange={e => setValue(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Enter') save(value);
          if (e.key === 'Escape') { setValue(current); setEditing(false); }
        }}
        className="flex-1 h-9 rounded-xl border border-[#e2e8f0] bg-white px-3 text-sm text-navy focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary" />
      <datalist id="crm-account-names">
        {accountNames.map(n => <option key={n} value={n} />)}
      </datalist>
      <button onClick={() => save(value)} disabled={saving}
        className="h-9 px-3 rounded-xl bg-primary text-white text-xs font-bold flex items-center gap-1 disabled:opacity-60">
        {saving ? <Loader size={12} className="animate-spin" /> : <Check size={12} />} {value.trim() ? 'Save' : 'Clear'}
      </button>
      <button onClick={() => { setValue(current); setEditing(false); }}
        className="h-9 px-3 rounded-xl border border-slate-200 bg-white text-slate-600 text-xs font-bold">Cancel</button>
    </div>
  );
}

const EXCLUSION_REASONS = Object.keys(EXCLUSION_REASON_LABELS) as CrmExclusionReason[];

// "No CRM needed": a booking that will never have an opportunity (internal
// session, training…) leaves the linking work and the coverage figure.
function ExcludeForm({ bookingId, onDone, onCancel }: {
  bookingId: string; onDone: (exclusion: BookingCrmExclusion) => void; onCancel: () => void;
}) {
  const [reason, setReason] = useState<CrmExclusionReason | ''>('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  async function save() {
    if (!reason) return toast.error('Choose a reason');
    if (reason === 'other' && !note.trim()) return toast.error('Add a note when the reason is Other');
    setSaving(true);
    try {
      onDone(await excludeBooking(bookingId, { reason, ...(note.trim() ? { note: note.trim() } : {}) }));
      toast.success('Marked as no CRM needed');
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Failed to update booking');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="bg-white border border-[#e2e8f0] rounded-xl p-4 flex flex-col gap-3">
      <p className="text-sm font-semibold text-navy">Why doesn't this booking need a CRM record?</p>
      <div className="flex flex-wrap gap-2">
        {EXCLUSION_REASONS.map(r => (
          <button key={r} type="button" onClick={() => setReason(r)}
            className={`px-3 py-1.5 rounded-lg border text-xs font-semibold transition-colors ${
              reason === r ? 'bg-primary text-white border-primary' : 'bg-white text-slate-600 border-[#e2e8f0] hover:bg-slate-50'
            }`}>
            {EXCLUSION_REASON_LABELS[r]}
          </button>
        ))}
      </div>
      <input value={note} onChange={e => setNote(e.target.value)} maxLength={200}
        placeholder={reason === 'other' ? 'Note (required)' : 'Note (optional)'}
        className="h-9 rounded-xl border border-[#e2e8f0] bg-white px-3 text-sm text-navy focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary" />
      <div className="flex justify-end gap-2">
        <button onClick={onCancel}
          className="h-8 px-3 rounded-lg border border-slate-200 bg-white text-slate-600 text-xs font-bold">Cancel</button>
        <button onClick={save} disabled={saving}
          className="h-8 px-3 rounded-lg bg-primary text-white text-xs font-bold flex items-center gap-1 disabled:opacity-60">
          {saving ? <Loader size={12} className="animate-spin" /> : <Ban size={12} />} Mark as no CRM needed
        </button>
      </div>
    </div>
  );
}

export default function CrmDrawer({
  booking, fullBooking, accountNames, conflictingIds, onClose,
  onAccountChanged, onOpportunityRemoved, onAddOpportunity, onEditOpportunity, onOpenBooking, onExclusionChanged,
  currentUser, onNotesSummary
}: Props) {
  const [excluding, setExcluding] = useState(false);
  const [including, setIncluding] = useState(false);

  useEffect(() => setExcluding(false), [booking.bookingId]);

  async function handleInclude() {
    setIncluding(true);
    try {
      await includeBooking(booking.bookingId);
      onExclusionChanged(null);
      toast.success('Included in CRM');
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Failed to update booking');
    } finally {
      setIncluding(false);
    }
  }

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function handleUnlink(link: BookingOpportunityLink) {
    if (!window.confirm(`Unlink "${effectiveValues(link).name}" from this booking?`)) return;
    try {
      await unlinkOpportunity(booking.bookingId, link.opportunityId);
      onOpportunityRemoved(link.opportunityId);
      toast.success('Opportunity unlinked');
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Failed to unlink opportunity');
    }
  }

  const opps = booking.crm.opportunities;
  const exclusion = booking.crm.exclusion;
  const dateText = booking.endDate && booking.endDate !== booking.startDate
    ? `${formatDate(booking.startDate)} – ${formatDate(booking.endDate)}`
    : `${formatDate(booking.startDate)} · ${DATE_TYPE_LABELS[booking.dateType] || ''}`;

  // Starts below the 64px app header (AdminLayout, z-50) so the header stays visible and usable
  return (
    <div className="fixed inset-x-0 bottom-0 top-16 z-40 flex justify-end bg-navy/20" onClick={onClose}>
      <aside className="w-full max-w-[520px] h-full bg-surface-light shadow-2xl flex flex-col font-display" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-start justify-between gap-4 px-6 py-5 bg-white border-b border-[#e2e8f0]">
          <div className="min-w-0">
            <h2 className="text-xl font-bold text-navy truncate">{customerLabel(booking)}</h2>
            <div className="flex items-center gap-2 mt-1">
              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${STATUS_COLORS[booking.status]}`}>
                {STATUS_LABELS[booking.status]}
              </span>
              <span className="text-xs text-slate-500">{formatDate(booking.startDate)}</span>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500"><X size={18} /></button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 flex flex-col gap-7">
          {/* Opportunities */}
          <section>
            <SectionLabel action={!exclusion && (
              <div className="flex items-center gap-2">
                {opps.length === 0 && !excluding && (
                  <button onClick={() => setExcluding(true)}
                    className="flex items-center gap-1.5 rounded-lg h-8 px-3 border border-slate-200 bg-white text-slate-600 text-xs font-bold hover:bg-slate-50 transition-colors">
                    <Ban size={14} /> No CRM needed
                  </button>
                )}
                <button onClick={onAddOpportunity}
                  className="flex items-center gap-1.5 rounded-lg h-8 px-3 bg-primary hover:bg-primary-dark text-white text-xs font-bold transition-colors">
                  <Plus size={14} /> Link opportunity
                </button>
              </div>
            )}>Opportunities</SectionLabel>

            {exclusion ? (
              <div className="flex items-start justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-navy flex items-center gap-1.5">
                    <Ban size={14} className="text-slate-400" /> No CRM needed · {EXCLUSION_REASON_LABELS[exclusion.reason]}
                  </p>
                  {exclusion.note && <p className="text-sm text-slate-600 mt-1 break-words">{exclusion.note}</p>}
                  <p className="text-[11px] text-slate-400 mt-1">
                    Marked by {exclusion.excludedByName}, {formatDate(exclusion.excludedAt.slice(0, 10))}. Not counted in linking coverage.
                  </p>
                </div>
                <button onClick={handleInclude} disabled={including}
                  className="flex items-center gap-1.5 rounded-lg h-8 px-3 border border-slate-200 bg-white text-slate-600 text-xs font-bold hover:bg-slate-50 flex-shrink-0 disabled:opacity-60">
                  {including ? <Loader size={14} className="animate-spin" /> : <RotateCcw size={14} />} Include in CRM
                </button>
              </div>
            ) : excluding ? (
              <ExcludeForm bookingId={booking.bookingId}
                onDone={e => { setExcluding(false); onExclusionChanged(e); }}
                onCancel={() => setExcluding(false)} />
            ) : opps.length === 0 ? (
              <div className="rounded-xl border border-dashed border-slate-300 bg-white px-4 py-5 text-center">
                <p className="text-sm text-slate-500">No opportunities linked yet.</p>
                <p className="text-xs text-slate-400 mt-1">Open the opportunity in Dynamics, copy the address bar, then click Link opportunity.</p>
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                {opps.map(link => {
                  const v = effectiveValues(link);
                  return (
                    <div key={link.opportunityId} className="bg-white border border-[#e2e8f0] rounded-xl px-4 py-3">
                      <div className="flex items-start justify-between gap-3">
                        <a href={opportunityUrl(link.opportunityId)} target="_blank" rel="noopener noreferrer"
                          className="font-semibold text-navy hover:text-primary inline-flex items-center gap-1 min-w-0">
                          <span className="truncate">{v.name}</span> <ExternalLink size={11} className="text-slate-400 flex-shrink-0" />
                        </a>
                        <div className="flex items-center flex-shrink-0">
                          <button onClick={() => onEditOpportunity(link)} title="Edit"
                            className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500"><Pencil size={14} /></button>
                          <button onClick={() => handleUnlink(link)} title="Unlink"
                            className="p-1.5 rounded-lg hover:bg-red-50 text-slate-500 hover:text-red-600"><Trash2 size={14} /></button>
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-2 mt-2">
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${STATE_COLORS[v.state]}`}>{STATE_LABELS[v.state]}</span>
                        <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{ATTRIBUTION_LABELS[link.attribution]}</span>
                        <span className="text-sm font-bold text-navy">{formatMoney(v.value, v.currency)}</span>
                        {v.stage && <span className="text-xs text-slate-500">{v.stage}</span>}
                        {v.closeDate && <span className="text-xs text-slate-400">closes {formatDate(v.closeDate)}</span>}
                      </div>
                      <div className="flex flex-wrap items-center gap-2 mt-2 text-[11px] text-slate-400">
                        {link.syncStatus === 'manual' && <span className="font-semibold px-1.5 rounded bg-slate-100 text-slate-500">MANUAL</span>}
                        {link.syncStatus === 'not_visible' && <span className="font-semibold px-1.5 rounded bg-amber-50 text-amber-700">NOT VISIBLE IN DYNAMICS</span>}
                        {link.syncStatus === 'ok' && link.current && (
                          <span>At link: {formatMoney(link.snapshot.value, link.snapshot.currency)} · {STATE_LABELS[link.snapshot.state]}</span>
                        )}
                        {conflictingIds.has(link.opportunityId) && (
                          <span title="Linked to another booking with different values or attribution. Totals use the most recently updated entry."
                            className="inline-flex items-center gap-0.5 font-semibold text-amber-700">
                            <AlertTriangle size={10} /> Differs on another booking
                          </span>
                        )}
                        <span>Linked by {link.linkedByName}, {formatDate(link.linkedAt.slice(0, 10))}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          {/* Notes + activity */}
          <section>
            <SectionLabel>Notes &amp; activity</SectionLabel>
            <CrmNotes booking={booking} currentUser={currentUser} onSummary={onNotesSummary} />
          </section>

          {/* Account */}
          <section>
            <SectionLabel>Dynamics account</SectionLabel>
            <AccountSection booking={booking} accountNames={accountNames} onChanged={onAccountChanged} />
            {booking.crm.account && booking.crm.account.accountName !== booking.customer && (
              <p className="text-xs text-slate-400 mt-2">Booked as “{booking.customer}”</p>
            )}
          </section>

          {/* Booking */}
          <section>
            <SectionLabel action={fullBooking && (
              <button onClick={onOpenBooking}
                className="flex items-center gap-1.5 rounded-lg h-8 px-3 border border-slate-200 bg-white text-slate-600 text-xs font-bold hover:bg-slate-50 transition-colors">
                <PenSquare size={14} /> View / edit booking
              </button>
            )}>Booking</SectionLabel>
            <div className="grid grid-cols-2 gap-4 bg-white border border-[#e2e8f0] rounded-xl p-4">
              <Fact icon={CalendarDays} label="When" value={dateText} />
              <Fact icon={MapPin} label="Room" value={booking.roomName || '—'} />
              <Fact icon={Tag} label="Event type" value={booking.eventTypeName} />
              <Fact icon={Briefcase} label="Sector" value={booking.sector || '—'} />
              <Fact icon={User} label="Lead facilitator" value={booking.facilitatorName} />
              <Fact icon={Building2} label="Billing" value={BILLING_LABELS[booking.billingType] || booking.billingType} />
              {fullBooking?.goal && (
                <div className="col-span-2"><Fact icon={Tag} label="Goal" value={fullBooking.goal} /></div>
              )}
            </div>
            {!fullBooking && (
              <p className="text-xs text-slate-400 mt-2">Full booking details are only shown for live bookings you can see on the Room Calendar.</p>
            )}
          </section>
        </div>
      </aside>
    </div>
  );
}
